import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, Gift, Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useParentScope } from "@/context/ParentContext";
import { EmptyState, ErrorState, Loading } from "@/components/States";
import { Button } from "@/components/ui/button";
import { PaginationControls } from "@/components/PaginationControls";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ContextualNav } from "@/components/ContextualNav";

const REWARD_PAGE_SIZE = 12;
const REDEMPTION_PAGE_SIZE = 20;

export default function ParentRewards() {
  const { student, students } = useParentScope();
  const [data, setData] = useState(null);
  const [coins, setCoins] = useState(null);
  const [exams, setExams] = useState([]);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", description: "", cost_coins: 1000, student_id: "", exam_id: "", condition: "none" });
  const [rewardOffset, setRewardOffset] = useState(0);
  const [redemptionOffset, setRedemptionOffset] = useState(0);

  const load = useCallback(async () => {
    setError("");
    try {
      const [r, c, e] = await Promise.all([
        api.get("/parent/rewards", {
          params: {
            reward_limit: REWARD_PAGE_SIZE,
            reward_offset: rewardOffset,
            redemption_limit: REDEMPTION_PAGE_SIZE,
            redemption_offset: redemptionOffset,
          },
        }),
        student ? api.get(`/parent/students/${student.id}/coins`) : Promise.resolve({ data: null }),
        student ? api.get("/parent/exams", { params: { student_id: student.id, limit: 100 } }) : Promise.resolve({ data: [] }),
      ]);
      setData(r.data);
      setCoins(c.data);
      setExams(e.data);
    } catch (err) {
      setError(apiError(err, "No pudimos cargar las recompensas."));
    }
  }, [student, rewardOffset, redemptionOffset]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (event) => {
    event.preventDefault();
    try {
      const condition_config = buildCondition(form);
      await api.post("/parent/rewards", {
        name: form.name,
        description: form.description,
        cost_coins: Number(form.cost_coins),
        student_id: condition_config ? (form.student_id || student?.id) : (form.student_id || null),
        condition_config,
      });
      toast.success("Recompensa creada");
      setForm({ name: "", description: "", cost_coins: 1000, student_id: "", exam_id: "", condition: "none" });
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const decide = async (id, status) => {
    try {
      await api.patch(`/parent/redemptions/${id}`, { status });
      toast.success("Canje actualizado");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const archive = async (id) => {
    try {
      await api.patch(`/parent/rewards/${id}`, { status: "archived" });
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading />;

  return (
    <div className="space-y-6" data-testid="parent-rewards-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Recompensas</h1>
        <p className="mt-1 text-sm text-slate-500">
          Tu defines las reglas. El estudiante solo puede canjear, nunca editar.
        </p>
      </div>

      {student && coins && (
        <div className="estudia-card flex flex-wrap items-center justify-between gap-4 p-5" data-testid="coins-summary">
          <div>
            <p className="text-sm font-semibold text-slate-600">Saldo de {student.first_name}</p>
            <p className="font-display text-3xl font-extrabold text-amber-600">
              {coins.balance} <span className="text-base font-bold text-slate-600">monedas</span>
            </p>
            <p className="mt-1 text-xs text-slate-600">
              Ledger auditado: {coins.ledger_balance} · Gaming Mode {coins.gaming_mode ? "activo" : "desactivado"}
            </p>
          </div>
          <Coins className="h-10 w-10 text-amber-400" />
        </div>
      )}

      <form onSubmit={create} className="estudia-card space-y-4 p-5" data-testid="reward-create-form">
        <h2 className="font-display text-lg font-bold text-slate-900">Nueva recompensa</h2>
        <div className="grid gap-4 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Label htmlFor="rname">Nombre</Label>
            <Input
              id="rname"
              required
              className="estudia-touch mt-1.5"
              placeholder="Salida al cine"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="reward-name-input"
            />
          </div>
          <div>
            <Label htmlFor="rcost">Coste en monedas</Label>
            <Input
              id="rcost"
              type="number"
              min={1}
              required
              className="estudia-touch mt-1.5"
              value={form.cost_coins}
              onChange={(e) => setForm({ ...form, cost_coins: e.target.value })}
              data-testid="reward-cost-input"
            />
          </div>
          <div>
            <Label htmlFor="rstudent">Para</Label>
            <select
              id="rstudent"
              className="estudia-touch mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
              value={form.student_id}
              onChange={(e) => setForm({ ...form, student_id: e.target.value })}
              data-testid="reward-student-select"
            >
              <option value="">Toda la familia</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.first_name}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-4">
            <Label htmlFor="rdesc">Descripcion (opcional)</Label>
            <Input
              id="rdesc"
              className="estudia-touch mt-1.5"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="reward-description-input"
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="rcondition">Condición positiva (opcional)</Label>
            <select id="rcondition" className="estudia-touch mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}>
              <option value="none">Sin condición adicional</option>
              <option value="effort">Completar 80% del plan</option>
              <option value="mock">Completar el simulacro</option>
              <option value="sessions">Completar 3 sesiones</option>
              <option value="improvement">Mejorar al menos 5%</option>
              <option value="revenge">Completar Revenge Mode</option>
              <option value="xp">Alcanzar 500 XP del ciclo</option>
            </select>
          </div>
          {form.condition !== "none" && (
            <div className="sm:col-span-2">
              <Label htmlFor="rexam">Ciclo de examen</Label>
              <select id="rexam" required className="estudia-touch mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm" value={form.exam_id} onChange={(e) => setForm({ ...form, exam_id: e.target.value })}>
                <option value="">Selecciona un examen de {student?.first_name || "tu hijo"}</option>
                {exams.map((exam) => <option key={exam.id} value={exam.id}>{exam.subject} · {exam.title}</option>)}
              </select>
            </div>
          )}
        </div>
        <Button
          type="submit"
          className="estudia-touch rounded-xl bg-blue-600 font-bold hover:bg-blue-700"
          data-testid="reward-create-submit-btn"
        >
          <Plus className="mr-2 h-4 w-4" /> Crear recompensa
        </Button>
      </form>

      <section data-testid="rewards-list">
        <h2 className="font-display text-xl font-bold text-slate-900">Catalogo</h2>
        {data.rewards.length === 0 ? (
          <EmptyState title="Sin recompensas" description="Crea la primera para motivar a tu hijo." />
        ) : (
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {data.rewards.map((reward) => (
              <div key={reward.id} className="estudia-card p-4" data-testid="reward-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-display font-bold text-slate-900">{reward.name}</p>
                    {reward.description && <p className="text-sm text-slate-500">{reward.description}</p>}
                    {reward.condition_config && <p className="mt-2 text-xs font-bold text-violet-600">Premia esfuerzo o progreso dentro de un ciclo</p>}
                  </div>
                  <span className="flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-sm font-bold text-amber-700">
                    <Gift className="h-3.5 w-3.5" /> {reward.cost_coins}
                  </span>
                </div>
                {reward.status === "active" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-3 text-slate-500"
                    onClick={() => archive(reward.id)}
                    data-testid={`archive-reward-${reward.id}`}
                  >
                    Archivar
                  </Button>
                )}
                {reward.status === "archived" && <p className="mt-3 text-xs text-slate-400">Archivada</p>}
              </div>
            ))}
          </div>
        )}
        <PaginationControls
          offset={rewardOffset}
          limit={REWARD_PAGE_SIZE}
          total={data.pagination?.rewards.total || data.rewards.length}
          onChange={setRewardOffset}
          label="recompensas"
        />
      </section>

      <section data-testid="redemptions-list">
        <h2 className="font-display text-xl font-bold text-slate-900">Canjes solicitados</h2>
        {data.redemptions.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">Sin canjes todavia.</p>
        ) : (
          <div className="mt-4 space-y-3">
            {data.redemptions.map((red) => (
              <div
                key={red.id}
                className="estudia-card flex flex-wrap items-center justify-between gap-3 p-4"
                data-testid="redemption-row"
              >
                <div>
                  <p className="font-semibold text-slate-800">{red.reward}</p>
                  <p className="text-xs text-slate-500">
                    {red.student} · {red.cost_coins} monedas · {new Date(red.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
                    {red.status}
                  </span>
                  {red.status === "requested" && (
                    <>
                      <Button size="sm" onClick={() => decide(red.id, "approved")} data-testid={`approve-${red.id}`}>
                        Aprobar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => decide(red.id, "rejected")}
                        data-testid={`reject-${red.id}`}
                      >
                        Rechazar
                      </Button>
                    </>
                  )}
                  {red.status === "approved" && (
                    <Button size="sm" variant="outline" onClick={() => decide(red.id, "delivered")}>
                      Marcar entregado
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <PaginationControls
          offset={redemptionOffset}
          limit={REDEMPTION_PAGE_SIZE}
          total={data.pagination?.redemptions.total || data.redemptions.length}
          onChange={setRedemptionOffset}
          label="canjes"
        />
      </section>

      {coins && (
        <section className="estudia-card p-5" data-testid="coin-ledger">
          <h2 className="font-display text-xl font-bold text-slate-900">Historial de monedas</h2>
          {coins.movements.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">Sin movimientos todavia.</p>
          ) : (
            <ul className="mt-4 divide-y divide-slate-100 text-sm">
              {coins.movements.map((movement) => (
                <li key={movement.id} className="flex items-center justify-between gap-3 py-2" data-testid="ledger-row">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-700">{movement.reason}</p>
                    <p className="text-xs text-slate-400">{new Date(movement.created_at).toLocaleString()}</p>
                  </div>
                  <span
                    className={`shrink-0 font-display font-bold ${
                      movement.amount > 0 ? "text-emerald-600" : "text-rose-600"
                    }`}
                  >
                    {movement.amount > 0 ? "+" : ""}
                    {movement.amount}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <ContextualNav
        title="Relaciona premios con avances reales"
        description="Consulta la actividad antes de ajustar la siguiente motivación."
        items={[
          { to: "/p", icon: "dashboard", label: "Revisar progreso", description: "Mira sesiones, aciertos y temas." },
          { to: "/p/estudiantes", icon: "students", label: "Ajustar modo de juego", description: "Activa o pausa monedas por estudiante." },
        ]}
      />
    </div>
  );
}

function buildCondition(form) {
  if (form.condition === "none") return null;
  const base = { exam_id: form.exam_id };
  if (form.condition === "effort") return { ...base, plan_completion_min: 80 };
  if (form.condition === "mock") return { ...base, mock_exam_required: true };
  if (form.condition === "sessions") return { ...base, sessions_min: 3 };
  if (form.condition === "improvement") return { ...base, improvement_min: 5 };
  if (form.condition === "revenge") return { ...base, correction_complete: true };
  return { ...base, cycle_xp_min: 500 };
}
